import { supabase } from '../lib/supabase';
import { Customer, CartItem, ReservationData, FeedbackData, OrderFormData, MenuItem } from '../types';

// --- Customer Management ---

export const createOrGetCustomer = async (
  name: string,
  phone: string,
  email?: string
): Promise<Customer> => {
  if (!phone || !phone.trim()) {
    throw new Error("Phone number is required. Please ask the user for their phone number.");
  }

  // Normalize phone to format like +92 301 2345678 or similar
  // Logic to accept +92 3XX XXXXXXX, +92 3XXXXXXXXX, or 03XXXXXXXXX and normalize to +92 format
  let cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.startsWith('03')) {
    cleanPhone = '92' + cleanPhone.slice(1);
  } else if (cleanPhone.startsWith('3') && cleanPhone.length === 10) {
    cleanPhone = '92' + cleanPhone;
  }

  if (cleanPhone.length !== 12 || !cleanPhone.startsWith('923')) {
    throw new Error(`Invalid mobile number format. Please use a valid Pakistani mobile format like 0301-2345678 or +92 301 2345678.`);
  }

  const formattedPhone = `+92 ${cleanPhone.slice(2, 5)} ${cleanPhone.slice(5)}`;
  const cleanEmail = email && email.trim() !== '' ? email.trim() : null;
  const cleanName = name.trim();

  try {
    // 2. Try to FIND existing customer first
    const { data: existing, error: fetchError } = await supabase
      .from('customers')
      .select('*')
      .eq('phone', formattedPhone)
      .maybeSingle();

    if (fetchError) {
      console.warn('[createOrGetCustomer] Fetch check failed:', fetchError.message);
    }

    if (existing) {
      // Update email if provided and different
      if (cleanEmail && existing.email !== cleanEmail) {
        await supabase
          .from('customers')
          .update({ email: cleanEmail })
          .eq('id', existing.id);
        existing.email = cleanEmail;
      }
      return existing;
    }

    // 3. Customer not found, INSERT new one
    const { data: newCustomer, error: insertError } = await supabase
      .from('customers')
      .insert([{
        full_name: cleanName,
        phone: formattedPhone,
        email: cleanEmail
      }])
      .select()
      .single();

    if (insertError) {
      if (insertError.code === '23505') {
        const { data: retryData } = await supabase
          .from('customers')
          .select('*')
          .eq('phone', formattedPhone)
          .maybeSingle();
        if (retryData) return retryData;
      }
      throw insertError;
    }

    return newCustomer;
  } catch (error: any) {
    console.error('[createOrGetCustomer] CRITICAL ERROR:', error.message || error);
    throw new Error(error.message || 'Failed to create customer profile');
  }
};

// --- Order Management ---

export const submitOrder = async (
  customer: Customer,
  cart: CartItem[],
  details: OrderFormData,
  subtotal: number
) => {
  if (!customer?.id) throw new Error('Invalid customer ID. Cannot place order.');

  const tax_amount = subtotal * 0.13;
  const total_amount = subtotal + tax_amount;
  const order_number = `ZPK-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(1000 + Math.random() * 9000)}`;

  // Prepare items JSON
  const itemsJson = cart.map(item => ({
    item_id: item.id,
    name: item.name,
    quantity: item.quantity,
    price: item.price,
    special_instructions: details.specialInstructions
  }));

  const { data, error } = await supabase
    .from('orders')
    .insert([{
      customer_id: customer.id,
      order_number,
      items: itemsJson,
      subtotal,
      tax_amount,
      total_amount,
      order_type: details.orderType || 'pickup',
      status: 'pending',
      pickup_time: details.pickupTime === 'ASAP' ? new Date(Date.now() + 20 * 60000).toISOString() : new Date(Date.now() + 60 * 60000).toISOString(),
      payment_method: details.paymentMethod,
      special_requests: details.specialInstructions
    }])
    .select()
    .single();

  if (error) {
    console.error('[submitOrder] DB Error:', error);
    throw new Error(`Order submission failed: ${error.message}`);
  }
  return data;
};

// --- Reservation Management ---

export const checkAvailability = async (date: string, time: string, partySize: number) => {
  const MAX_CAPACITY_PER_SLOT = 15;

  const { count, error } = await supabase
    .from('reservations')
    .select('*', { count: 'exact', head: true })
    .eq('reservation_date', date)
    .eq('reservation_time', time)
    .neq('status', 'cancelled');

  if (error) throw error;

  const currentCount = count || 0;
  const percentageFull = Math.round((currentCount / MAX_CAPACITY_PER_SLOT) * 100);
  let busynessDescription = "Quiet";

  if (percentageFull >= 90) busynessDescription = "Almost Fully Booked";
  else if (percentageFull >= 70) busynessDescription = "Very Busy";
  else if (percentageFull >= 40) busynessDescription = "Moderately Busy";
  else busynessDescription = "Plenty of Availability";

  return {
    available: currentCount < MAX_CAPACITY_PER_SLOT,
    count: currentCount,
    percentage: percentageFull,
    description: busynessDescription,
    alternative_times: ['17:00', '19:00', '20:00']
  };
};

export const lookupReservations = async (phone: string) => {
  // Normalize phone
  let cleanPhoneDigits = phone.replace(/\D/g, '');
  if (cleanPhoneDigits.startsWith('03')) cleanPhoneDigits = '92' + cleanPhoneDigits.slice(1);
  else if (cleanPhoneDigits.startsWith('3') && cleanPhoneDigits.length === 10) cleanPhoneDigits = '92' + cleanPhoneDigits;

  const searchPhone = `+92 ${cleanPhoneDigits.slice(2, 5)} ${cleanPhoneDigits.slice(5)}`;

  // 2. Find customer
  const { data: customer } = await supabase
    .from('customers')
    .select('id, full_name')
    .eq('phone', searchPhone)
    .maybeSingle();

  if (!customer) return [];

  // 3. Find future reservations
  const today = new Date().toISOString().split('T')[0];
  const { data: reservations } = await supabase
    .from('reservations')
    .select('*')
    .eq('customer_id', customer.id)
    .gte('reservation_date', today)
    .neq('status', 'cancelled')
    .order('reservation_date', { ascending: true });

  return reservations || [];
};

export const createReservation = async (
  customer: Customer,
  data: ReservationData
) => {
  if (!customer?.id) throw new Error('Invalid customer ID. Cannot book reservation.');

  const availability = await checkAvailability(data.date, data.time, data.guests);

  if (!availability.available) {
    throw new Error('Selected time slot is full. Please choose another time.');
  }

  const reservation_code = `RES-ZPK-${data.date.replace(/-/g, '')}-${Math.floor(1000 + Math.random() * 9000)}`;

  const { data: res, error } = await supabase
    .from('reservations')
    .insert([{
      customer_id: customer.id,
      reservation_code,
      reservation_date: data.date,
      reservation_time: data.time,
      party_size: data.guests,
      status: 'confirmed',
      occasion: data.occasion,
      seating_preference: data.seating_preference,
      special_requests: data.specialRequests
    }])
    .select()
    .single();

  if (error) {
    console.error('[createReservation] DB Error:', error);
    throw new Error(`Reservation failed: ${error.message}`);
  }
  return res;
};

// --- Review Management ---

export const submitReview = async (
  customer: Customer | null,
  data: FeedbackData
) => {
  const { data: review, error } = await supabase
    .from('reviews')
    .insert([{
      customer_id: customer?.id || null,
      rating: data.rating,
      review_text: data.review,
      categories: data.tags,
      is_anonymous: !data.showName,
      display_name: data.showName && customer ? customer.full_name : 'Anonymous',
      is_approved: false
    }])
    .select()
    .single();

  if (error) throw error;
  return review;
};

// --- Data Fetching ---

export const getMenuItems = async (): Promise<MenuItem[]> => {
  const { data, error } = await supabase
    .from('menu_items')
    .select('*')
    .eq('is_available', true);

  if (error) {
    console.error('Error fetching menu:', error);
    return [];
  }

  return (data || []).map((item: any) => ({
    ...item,
    image: item.image_url || item.image
  }));
};

export const getFeaturedItems = async (): Promise<MenuItem[]> => {
  // Try to fetch items explicitly marked as popular first
  let { data, error } = await supabase
    .from('menu_items')
    .select('*')
    .eq('is_available', true)
    .eq('popular', true)
    .limit(4);

  // If we don't have enough popular items, fill with other available items
  if (!data || data.length < 4) {
    const { data: fillData } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_available', true)
      .order('price', { ascending: false }) // Fallback to expensive/main items often implies signature dishes
      .limit(4);

    const existingIds = new Set(data?.map(i => i.id));
    const uniqueFill = (fillData || []).filter(i => !existingIds.has(i.id));
    data = [...(data || []), ...uniqueFill].slice(0, 4);
  }

  if (error && (!data || data.length === 0)) {
    console.error('Error fetching featured:', error);
    return [];
  }

  return (data || []).map((item: any) => ({
    ...item,
    image: item.image_url || item.image
  }));
};

export const getReviews = async () => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      id,
      rating,
      review_text,
      categories,
      is_anonymous,
      display_name,
      created_at
    `)
    .eq('is_approved', true)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) {
    console.error('Error fetching reviews:', error);
    return [];
  }

  return data.map(r => ({
    ...r,
    author: r.is_anonymous ? 'Anonymous Diner' : r.display_name,
    text: r.review_text,
    date: new Date(r.created_at).toLocaleDateString()
  })) || [];
};